/**
 * Game Controller for Poker Reborn
 *
 * Manages hand flow: dealing, betting rounds, showdown.
 */

import { networkService } from '@/services/NetworkService';
import { EventEmitter } from '@/services/EventEmitter';
import { audioService } from '@/services/AudioService';
import { log } from '@/utils/log';
import { GAME_FLOW_TIMING } from '@/config/animation';
import { gameEventBus } from '@/effects/events';
import { getHandName, getCoreCardCount } from '@/utils/poker';
import {
  tableState,
  setActivePlayer,
  setStreet,
  setCommunityCards,
  addCommunityCards,
  setPots,
  setHeroCards,
  setHeroActions,
  startHand,
  endHand,
  updatePlayer,
  getPlayer,
  setLastStreetAction,
  getLastStreetAction,
  clearLastStreetAction,
  isBrUpdated,
} from '@/state/TableState';
import {
  streetEndSequencer,
  gameEndSequencer,
  type GameEndMessage,
  type EndPlayerInfo,
  type PotAwardInfo,
  type PlayerPotSource,
} from '@/effects/sequencer';
import { targetRegistry } from '@/effects/registry/TargetRegistry';
// NOTE: ChipBetEffect moved to TableScreen for proper animation control
// @see tasks/active/T22_BET_CHIP_ANIMATION_PLAN.md
import type {
  Card,
  ActionType,
  AvailableActions,
  Street,
  PotState,
  PlayerState,
  CardCode,
  PreActionSelection,
} from '@/types/game';

// =============================================================================
// TYPES
// =============================================================================

export interface GameControllerEvents {
  /** Hand started */
  handStarted: { gameNumber: number; dealerSeat: number };

  /** Hole cards dealt to hero */
  holeCardsDealt: { cards: Card[] };

  /** Hole cards dealt to opponent (face down) */
  opponentCardsDealt: { seat: number };

  /** Blinds posted */
  blindsPosted: { smallBlind: { seat: number; amount: number }; bigBlind: { seat: number; amount: number } };

  /** Action required from hero */
  actionRequired: AvailableActions;

  /** Player acted */
  playerActed: { seat: number; action: ActionType; amount: number };

  /** Street changed */
  streetChanged: { street: Street; cards?: Card[] };

  /** Pot updated */
  potUpdated: { pots: PotState[] };

  /** Showdown - reveal cards */
  showdown: { playerCards: Map<number, [Card, Card]> };

  /** Player cards revealed (from show message) */
  cardsRevealed: { seat: number; cards: [Card, Card] };

  /** Winner declared */
  winner: { seat: number; amount: number; handName?: string };

  /** Winning hand cards (for highlighting) and hand name */
  winningHand: { cards: Card[]; handName?: string };

  /** Hand ended */
  handEnded: void;

  /** Timer tick */
  timerTick: { seat: number; remaining: number; total: number };

  /** Showdown prompt - hero can show or muck cards */
  showdownPrompt: void;

  /** Cards mucked (player chose not to show) */
  cardsMucked: { seat: number };

  /** Error occurred */
  error: { message: string };
}

// =============================================================================
// GAME CONTROLLER
// =============================================================================

/** Delay before playing "your turn" reminder sound (ms) */
const ACT_SOUND_DELAY = 5000;

class GameController extends EventEmitter<GameControllerEvents> {
  private static instance: GameController | null = null;
  private actionTimer: ReturnType<typeof setTimeout> | null = null;
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private timerRemaining = 0;
  private timerTotal = 0;

  /** Timer for delayed "your turn" reminder sound */
  private actSoundTimer: ReturnType<typeof setTimeout> | null = null;

  /** Accumulated hole cards (server sends 2 separate messages) */
  private pendingHoleCards: Card[] = [];

  /** Tracks if any betting actions occurred on current street (for GAME_END detection) */
  private _hadBettingOnCurrentStreet = false;

  /** Tracks the last action type on current street (for GAME_END) */
  private _lastActionOnCurrentStreet: ActionType | null = null;

  /** Callback to check for a queued pre-action from the UI */
  onCheckPreAction: (() => PreActionSelection | null) | null = null;

  /** Callback to clear the queued pre-action after execution or invalidation */
  onClearPreAction: (() => void) | null = null;

  private constructor() {
    super();
    this.setupNetworkHandlers();
  }

  /** Get singleton instance */
  static getInstance(): GameController {
    if (!GameController.instance) {
      GameController.instance = new GameController();
    }
    return GameController.instance;
  }

  // ===========================================================================
  // PUBLIC METHODS - ACTIONS
  // ===========================================================================

  /**
   * Send fold action
   */
  fold(): void {
    log.game.debug`GameController.fold() called`;
    this.sendAction('fold');
  }

  /**
   * Send check action
   */
  check(): void {
    log.game.debug`GameController.check() called`;
    this.sendAction('check');
  }

  /**
   * Send call action
   */
  call(): void {
    log.game.debug`GameController.call() called`;
    this.sendAction('call');
  }

  /**
   * Send bet action with amount (first bet on a street)
   */
  bet(amount: number): void {
    log.game.debug`GameController.bet(${amount}) called`;
    this.sendAction('bet', amount);
  }

  /**
   * Send raise action with amount
   */
  raise(amount: number): void {
    log.game.debug`GameController.raise(${amount}) called`;
    this.sendAction('raise', amount);
  }

  /**
   * Send all-in action
   */
  allIn(): void {
    log.game.debug`GameController.allIn() called`;
    this.sendAction('allin');
  }

  /**
   * Send show action (reveal cards at showdown)
   */
  show(): void {
    log.game.info`GameController.show() called`;
    const croupierId = tableState.tableId.get();
    if (!croupierId) {
      log.game.error`Cannot send show - no croupier ID`;
      return;
    }
    networkService.send(croupierId, '# show');
  }

  /**
   * Send muck action (hide cards at showdown)
   */
  muck(): void {
    log.game.info`GameController.muck() called`;
    const croupierId = tableState.tableId.get();
    if (!croupierId) {
      log.game.error`Cannot send muck - no croupier ID`;
      return;
    }
    networkService.send(croupierId, '# muck');
  }

  // ===========================================================================
  // DEVELOPMENT - MOCK HAND
  // ===========================================================================

  /**
   * Start a mock hand for development/testing
   */
  async startMockHand(): Promise<void> {
    const heroSeat = tableState.heroSeat.get();
    if (heroSeat === null) {
      this.emit('error', { message: 'Must be seated to start hand' });
      return;
    }

    // Get players
    const players = tableState.players.get();
    if (players.size < 2) {
      this.emit('error', { message: 'Need at least 2 players' });
      return;
    }

    // Start hand
    const gameNumber = Math.floor(Math.random() * 1000000);
    const dealerSeat = this.findNextOccupiedSeat(-1, players);

    startHand(gameNumber, dealerSeat);
    this.emit('handStarted', { gameNumber, dealerSeat });

    // Post blinds
    await this.delay(500);
    const sbSeat = this.findNextOccupiedSeat(dealerSeat, players);
    const bbSeat = this.findNextOccupiedSeat(sbSeat, players);
    const stakes = tableState.stakes.get();

    this.postBlind(sbSeat, stakes.smallBlind, 'sb');
    await this.delay(300);
    this.postBlind(bbSeat, stakes.bigBlind, 'bb');

    this.emit('blindsPosted', {
      smallBlind: { seat: sbSeat, amount: stakes.smallBlind },
      bigBlind: { seat: bbSeat, amount: stakes.bigBlind },
    });

    // Deal hole cards
    await this.delay(500);
    const heroCards = this.generateRandomCards(tableState.holeCardCount.get());
    setHeroCards(heroCards);
    this.emit('holeCardsDealt', { cards: heroCards });

    // Emit opponent cards dealt events
    players.forEach((_, seat) => {
      if (seat !== heroSeat) {
        this.emit('opponentCardsDealt', { seat });
      }
    });

    // Set up pot
    setPots([{ id: 0, amount: stakes.smallBlind + stakes.bigBlind, eligiblePlayerIds: [] }]);
    this.emit('potUpdated', { pots: tableState.pots.get() });

    // Start action with UTG (next after BB)
    await this.delay(500);
    const utgSeat = this.findNextOccupiedSeat(bbSeat, players);
    this.startAction(utgSeat, players);
  }

  /**
   * Continue mock hand with next action (for non-hero)
   */
  mockOpponentAction(): void {
    const activeSeat = tableState.activeSeat.get();
    const heroSeat = tableState.heroSeat.get();
    const players = tableState.players.get();

    if (activeSeat === null || activeSeat === heroSeat) {
      return;
    }

    // Random action: mostly call/check
    const actions: ActionType[] = ['call', 'check', 'fold', 'raise'];
    const weights = [0.4, 0.3, 0.15, 0.15];
    const action = this.weightedRandom(actions, weights);
    const amount = action === 'raise' ? tableState.stakes.get().bigBlind * 3 : 0;

    this.handlePlayerAction(activeSeat, action, amount);

    // P0.1.2: Mock-only delay (development mode - not a production blocking pattern)
    // Move to next player or next street after mock delay
    setTimeout(() => {
      const nextSeat = this.findNextActivePlayer(activeSeat, players);
      if (nextSeat !== null && this.isBettingContinuing(players)) {
        this.startAction(nextSeat, players);
      } else {
        this.advanceStreet();
      }
    }, GAME_FLOW_TIMING.mockOpponentActionDelay);
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private setupNetworkHandlers(): void {
    networkService.on('message', ({ from, body }) => {
      try {
        const message = JSON.parse(body) as Record<string, unknown>;
        this.handleNetworkMessage(from, message);
      } catch {
        // Not a JSON message we handle
      }
    });
  }

  private handleNetworkMessage(from: string, message: Record<string, unknown>): void {
    // Source validation: Only process game events from current table's croupier
    // tableState.tableId contains the croupier GLID when at a table
    const currentCroupier = tableState.tableId.get();
    if (currentCroupier !== null && from !== currentCroupier) {
      log.game.debug`Ignoring game event from ${from} (current croupier: ${currentCroupier})`;
      return;
    }

    switch (message.a) {
      case 'start':
        this.handleStart(message);
        break;
      case 'blinds':
        this.handleBlinds(message);
        break;
      case 'card':
        this.handleCard(message);
        break;
      case 'act':
        this.handleAct(message);
        break;
      case 'action':
        this.handleAction(message);
        break;
      // POKERTALK action messages - player performed an action
      case 'bet':
      case 'call':
      case 'raise':
      case 'fold':
      case 'check':
      case 'allin':
        this.handlePlayerActionMessage(message);
        break;
      case 'flop':
        this.handleFlop(message);
        break;
      case 'turn':
        this.handleTurn(message);
        break;
      case 'river':
        this.handleRiver(message);
        break;
      case 'show':
        this.handleShow(message);
        break;
      case 'win':
        this.handleWin(message);
        break;
      case 'end':
        this.handleEnd(message);
        break;
      case 'showdown':
        this.handleShowdownPrompt(message);
        break;
      case 'muck':
        this.handleMuck(message);
        break;
    }
  }

  private handleStart(message: Record<string, unknown>): void {
    const gameNumber = Number(message.gn ?? 0);

    // Cancel any pending act sound from previous hand
    this.cancelActSoundTimer();

    // Reset betting/action tracking for new hand
    this._hadBettingOnCurrentStreet = false;
    this._lastActionOnCurrentStreet = null;

    // Find dealer seat from players array (player with pos: "btn")
    // Per POKERTALK.md: players[].pos indicates position ("btn", "sb", "bb", etc.)
    // Note: Server uses 1-based seats, client uses 0-based
    const players = message.players as Array<{ sn: number; pos: string }> | undefined;
    let dealerSeat = 0;
    if (players) {
      const dealer = players.find(p => p.pos === 'btn');
      if (dealer) {
        dealerSeat = dealer.sn - 1; // Convert to 0-based
      }
    }

    // Read hole card count from gameInfo (2 for Hold'em, 4 for Omaha)
    const gameInfo = message.gameInfo as Record<string, unknown> | undefined;
    const holeCardCount = Number(gameInfo?.hole_cards ?? 2);
    tableState.holeCardCount.set(holeCardCount);

    // Clear pending hole cards for new hand
    this.pendingHoleCards = [];

    startHand(gameNumber, dealerSeat);
    this.emit('handStarted', { gameNumber, dealerSeat });

    // P1.3.2: Emit to GameEventBus for Effects Engine
    gameEventBus.emit('hand:start', { gameNumber, dealerSeat });

    log.game.info`Hand #${gameNumber} started, dealer at seat ${dealerSeat}`;
  }

  private handleBlinds(message: Record<string, unknown>): void {
    // POKERTALK format:
    // {"a":"blinds","sb":{"player":{"id":"player2"},"blind":5,"br":857},
    //  "bb":[{"player":{"id":"player3"},"blind":10,"br":841}]}
    const sbData = message.sb as { player: { id: string }; blind: number; br: number } | undefined;
    const bbData = message.bb as Array<{ player: { id: string }; blind: number; br: number }> | undefined;

    const heroSeat = tableState.heroSeat.get();
    const players = tableState.players.get();

    // Helper to find seat by player ID
    const findSeat = (playerId: string): number | null => {
      if (playerId === 'you') return heroSeat;
      for (const [seat, player] of players) {
        if (player.profile.id === playerId) return seat;
      }
      return null;
    };

    // Process small blind
    if (sbData) {
      const sbSeat = findSeat(sbData.player.id);
      if (sbSeat !== null) {
        const playerState = getPlayer(sbSeat);
        if (playerState) {
          updatePlayer(sbSeat, {
            ...playerState,
            bet: sbData.blind,
            stack: sbData.br,
            position: 'sb',
          });
          log.game.debug`Small blind posted: seat ${sbSeat}, amount ${sbData.blind}`;
        }
      }
    }

    // Process big blind(s)
    if (bbData && Array.isArray(bbData)) {
      for (const bb of bbData) {
        const bbSeat = findSeat(bb.player.id);
        if (bbSeat !== null) {
          const playerState = getPlayer(bbSeat);
          if (playerState) {
            updatePlayer(bbSeat, {
              ...playerState,
              bet: bb.blind,
              stack: bb.br,
              position: 'bb',
            });
            log.game.debug`Big blind posted: seat ${bbSeat}, amount ${bb.blind}`;
          }
        }
      }
    }

    // Emit blindsPosted event for Effects Engine
    if (sbData && bbData && bbData.length > 0) {
      const sbSeat = findSeat(sbData.player.id);
      const bbSeat = findSeat(bbData[0]?.player.id ?? '');

      if (sbSeat !== null && bbSeat !== null) {
        this.emit('blindsPosted', {
          smallBlind: { seat: sbSeat, amount: sbData.blind },
          bigBlind: { seat: bbSeat, amount: bbData[0]?.blind ?? 0 },
        });

        // P1.3.2: Emit blinds:posted to GameEventBus for Effects Engine
        gameEventBus.emit('blinds:posted', {
          smallBlind: { seat: sbSeat, amount: sbData.blind, playerId: sbData.player.id },
          bigBlind: { seat: bbSeat, amount: bbData[0]?.blind ?? 0, playerId: bbData[0]?.player.id ?? '' },
        });
      }
    }

    log.game.info`Blinds posted`;
  }

  private handleCard(message: Record<string, unknown>): void {
    // Server sends separate card messages, one per hole card
    // Hold'em: 2 messages, Omaha: 4 messages
    // Format: {"a":"card","player":{"id":"you"},"card":"8d"}
    const cardCode = message.card as string | undefined;
    if (!cardCode) return;

    const card = this.parseCardCode(cardCode as CardCode);
    if (!card) return;

    // Accumulate cards
    const expectedCount = tableState.holeCardCount.get();
    this.pendingHoleCards.push(card);
    log.game.debug`Received hole card: ${cardCode} (${this.pendingHoleCards.length}/${expectedCount})`;

    // When we have all cards, update state and emit event
    if (this.pendingHoleCards.length === expectedCount) {
      const holeCards = [...this.pendingHoleCards];
      setHeroCards(holeCards);
      this.emit('holeCardsDealt', { cards: holeCards });

      // Mark hero player as having cards (startHand resets hasCards to false)
      const heroSeat = tableState.heroSeat.get();
      if (heroSeat !== null) {
        const heroPlayer = getPlayer(heroSeat);
        if (heroPlayer && !heroPlayer.hasCards) {
          updatePlayer(heroSeat, { ...heroPlayer, hasCards: true });
        }
      }

      // P1.3.3: Emit to GameEventBus for Effects Engine
      if (heroSeat !== null) {
        gameEventBus.emit('cards:dealt:hero', { cards: holeCards, seat: heroSeat });
      }

      const cardSummary = holeCards.map((c) => `${c.rank}${c.suit}`).join(', ');
      log.game.info`Hole cards dealt: ${cardSummary}`;
    }
  }

  private handleAct(message: Record<string, unknown>): void {
    // POKERTALK format:
    // {"a":"act","time_to_act":15,"player":{"id":"player9"},"br":301,"pps":0,
    //  "acts":[{"act":"fold","min_amount":0,"max_amount":0},{"act":"call","min_amount":2,"max_amount":2},...]}

    const player = message.player as { id: string } | undefined;
    const timeout = Number(message.time_to_act ?? 30);
    const acts = message.acts as Array<{ act: string; min_amount: number; max_amount: number }> | undefined;

    // Check if it's the hero (player.id === "you" indicates hero in POKERTALK)
    const heroSeat = tableState.heroSeat.get();
    const isHero = player?.id === 'you';

    // Find the seat for this player
    let seat: number | null = null;
    if (isHero) {
      // For hero, use heroSeat directly
      seat = heroSeat;
    } else {
      // Find seat by player ID
      const players = tableState.players.get();
      players.forEach((p, s) => {
        if (p.profile.id === player?.id) {
          seat = s;
        }
      });
    }

    log.game.debug`handleAct: player=${player?.id}, seat=${seat}, isHero=${isHero}, heroSeat=${heroSeat}, acts=${acts?.map(a => a.act).join(',') ?? 'none'}`;

    if (seat === null) {
      log.game.warning`Could not find seat for player ${player?.id}`;
      return;
    }

    // Capture seat as non-null for use in closures
    const seatNumber = seat;

    // Parse hero actions if available
    let availableActions: AvailableActions | undefined;
    if (isHero && acts) {
      const actionTypes: ActionType[] = acts.map((a) => a.act as ActionType);
      let callAmount = 0;
      let minRaise = 0;
      let maxRaise = 0;

      for (const a of acts) {
        if (a.act === 'call') {
          callAmount = a.min_amount;
        } else if (a.act === 'raise' || a.act === 'bet') {
          minRaise = a.min_amount;
          maxRaise = a.max_amount;
        }
      }

      availableActions = {
        actions: actionTypes,
        callAmount,
        minRaise,
        maxRaise,
        timeout,
      };
    }

    // Function to activate player's turn (seat highlight, timer, events)
    const activateTurn = () => {
      // Check if this seat was already active (TableController's 'state' message may have handled it)
      const wasAlreadyActive = tableState.activeSeat.get() === seatNumber;

      setActivePlayer(seatNumber);
      log.game.debug`Active player set to seat ${seatNumber}`;

      // Start timer for ALL players (hero and opponents) so timer ring animates
      // Always restart timer since 'act' message has authoritative timeout value
      this.startTimer(seatNumber, timeout);

      // Only emit turn:started if this seat wasn't already active
      // This prevents double timer ring initialization when both 'state' and 'act' messages arrive
      if (!wasAlreadyActive) {
        gameEventBus.emit('turn:started', {
          seat: seatNumber,
          playerId: isHero ? 'hero' : (player?.id ?? ''),
          timeout,
          isHero,
          availableActions,
        });
      }

      // Hero-specific: check pre-action or show action panel
      if (isHero && availableActions) {
        // Check for queued pre-action
        const preAction = this.onCheckPreAction?.() ?? null;
        if (preAction && this.isPreActionValid(preAction, availableActions)) {
          log.game.info`Executing pre-action: ${preAction.type}`;
          this.onClearPreAction?.();
          this.executePreAction(preAction, availableActions);
          return;
        }

        // Clear stale pre-action selection
        this.onClearPreAction?.();

        // Show normal action panel
        setHeroActions(availableActions);
        this.emit('actionRequired', availableActions);
        log.game.info`Your turn! Actions: ${availableActions.actions.join(', ')}, timeout: ${timeout}s`;

        // Start delayed "your turn" reminder sound (plays after 5s if no action)
        this.startActSoundTimer();
      }
    };

    // Check if delay is needed (first to act on post-flop street) - applies to ANY player
    const street = tableState.street.get();
    const isPostFlop = street === 'flop' || street === 'turn' || street === 'river';
    const isFirstToAct = this._lastActionOnCurrentStreet === null;

    if (isPostFlop && isFirstToAct) {
      log.game.debug`Delaying turn activation for seat ${seatNumber} (first to act on ${street})`;
      setTimeout(activateTurn, GAME_FLOW_TIMING.actionPanelFirstToActDelay);
    } else {
      activateTurn();
    }
  }

  private handleAction(message: Record<string, unknown>): void {
    const seat = Number(message.seat ?? 0);
    const action = (message.action as ActionType) ?? 'fold';
    const amount = Number(message.amount ?? 0);

    this.handlePlayerAction(seat, action, amount);
  }

  /** Handle POKERTALK action messages (bet/call/raise/fold/check/allin) */
  private handlePlayerActionMessage(message: Record<string, unknown>): void {
    // POKERTALK format:
    // {"a":"bet","player":{"id":"you"},"bet":10,"pp":10,"pps":2,"br":790}
    // {"a":"fold","player":{"id":"player2"},"bet":0,"pp":0,"pps":0,"br":500}
    const actionType = message.a as ActionType;
    const player = message.player as { id: string } | undefined;
    const betAmount = Number(message.bet ?? 0);
    const totalPot = Number(message.pp ?? 0); // Player's total pot contribution
    const bankroll = Number(message.br ?? 0);

    if (!player) return;

    // Find the seat for this player
    const players = tableState.players.get();
    let seat: number | null = null;
    players.forEach((p, s) => {
      if (p.profile.id === player.id) {
        seat = s;
      }
    });

    // If player id is "you", check hero seat
    if (player.id === 'you') {
      seat = tableState.heroSeat.get();
    }

    if (seat === null) {
      log.game.warning`Action from unknown player: ${player.id}`;
      return;
    }

    // Update player state
    const playerState = getPlayer(seat);
    if (playerState) {
      const updatedPlayer: PlayerState = {
        ...playerState,
        lastAction: actionType,
        stack: bankroll,
      };

      // Only update bet for betting actions (bet/call/raise/allin)
      // Check and fold don't change the current street's bet display
      let animationAmount = 0;
      if (actionType === 'fold') {
        updatedPlayer.status = 'folded';
        // Keep existing bet - will be collected when street ends
      } else if (actionType === 'check') {
        // Check doesn't change bet - keep existing value
      } else if (actionType === 'allin') {
        updatedPlayer.status = 'allin';
        animationAmount = totalPot - playerState.bet; // Amount being added
        updatedPlayer.bet = totalPot;
      } else {
        // bet, call, raise - use the action's bet amount
        animationAmount = betAmount;
        updatedPlayer.bet = playerState.bet + betAmount;
      }

      // Debug: track bet changes (suspended - too noisy)
      // import('@/utils/DebugOverlay').then(({ debugOverlay }) => {
      //   debugOverlay.log(`ACTION ${actionType}: seat ${seat} bet ${playerState.bet} → ${updatedPlayer.bet}`);
      // });

      // Emit pre-event for chip animation BEFORE state update
      // This allows TableScreen to hide the player pot before state subscription shows it
      // @see tasks/active/T22_BET_CHIP_ANIMATION_PLAN.md
      if (animationAmount > 0 && ['bet', 'call', 'raise', 'allin'].includes(actionType)) {
        gameEventBus.emit('player:betting:start', {
          seat,
          amount: animationAmount,
          action: actionType as 'bet' | 'call' | 'raise' | 'allin',
        });
      }

      updatePlayer(seat, updatedPlayer);

      // Track actions for GAME_END detection
      this._lastActionOnCurrentStreet = actionType;
      if (['bet', 'call', 'raise', 'allin'].includes(actionType)) {
        this._hadBettingOnCurrentStreet = true;
      }

      // NOTE: Chip animation is now handled by TableScreen via 'player:betting:start' event
      // @see tasks/active/T22_BET_CHIP_ANIMATION_PLAN.md
    }

    // Clear hero actions if hero acted
    const heroSeat = tableState.heroSeat.get();
    if (seat === heroSeat || player.id === 'you') {
      setHeroActions(null);
    }

    this.stopTimer();
    this.emit('playerActed', { seat, action: actionType, amount: betAmount });

    // P1.3.7: Emit player action to GameEventBus for Effects Engine
    const actionPayload = {
      seat,
      playerId: player.id,
      amount: betAmount,
      stackAfter: bankroll,
      totalBet: totalPot,
    };

    // Emit specific action event
    switch (actionType) {
      case 'fold':
        gameEventBus.emit('player:fold', { ...actionPayload, action: 'fold' });
        break;
      case 'check':
        gameEventBus.emit('player:check', { ...actionPayload, action: 'check' });
        break;
      case 'call':
        gameEventBus.emit('player:call', { ...actionPayload, action: 'call' });
        break;
      case 'bet':
        gameEventBus.emit('player:bet', { ...actionPayload, action: 'bet' });
        break;
      case 'raise':
        gameEventBus.emit('player:raise', { ...actionPayload, action: 'raise', raiseAmount: betAmount });
        break;
      case 'allin':
        gameEventBus.emit('player:allin', { ...actionPayload, action: 'allin' });
        break;
    }

    // Also emit the generic player:acted event
    gameEventBus.emit('player:acted', { ...actionPayload, action: actionType } as Parameters<typeof gameEventBus.emit<'player:acted'>>[1]);

    // Emit turn:ended
    gameEventBus.emit('turn:ended', { seat, reason: 'action' });

    // Track last action for street-end animation sequence
    setLastStreetAction(seat, actionType, betAmount, player.id);

    log.game.info`${player.id} ${actionType}${betAmount > 0 ? ` ${betAmount}` : ''}`;
  }

  private handleFlop(message: Record<string, unknown>): void {
    // POKERTALK: {"a":"flop","cards":["3d","5h","6h"],"pots":[{"id":1,"pot_gross":30,...}]}
    const cards = message.cards as string[] | undefined;
    if (!cards || cards.length !== 3) return;

    // Reset betting/action tracking for new street
    this._hadBettingOnCurrentStreet = false;
    this._lastActionOnCurrentStreet = null;

    const flopCards: Card[] = cards
      .map((c) => this.parseCardCode(c as CardCode))
      .filter((c): c is Card => c !== null);

    // Get and clear last action for animation sequence
    const lastAction = getLastStreetAction();
    clearLastStreetAction();

    // Update state immediately (fire-and-forget philosophy)
    setStreet('flop');
    setCommunityCards(flopCards);
    this.updatePotsFromMessage(message);
    this.emit('streetChanged', { street: 'flop', cards: flopCards });

    // Play street-end animation sequence (fire-and-forget)
    streetEndSequencer.playSequence(
      { street: 'flop', cards: flopCards },
      lastAction
    );

    // P1.3.4: Emit flop:revealed to GameEventBus for Effects Engine
    if (flopCards.length === 3) {
      gameEventBus.emit('flop:revealed', {
        cards: flopCards as [Card, Card, Card],
      });
    }

    log.game.info`Flop: ${cards.join(', ')}`;
  }

  private handleTurn(message: Record<string, unknown>): void {
    // POKERTALK: {"a":"turn","cards":["9c"],"pots":[...]}
    const cards = message.cards as string[] | undefined;
    if (!cards || cards.length < 1) return;

    const card = this.parseCardCode(cards[0] as CardCode);
    if (!card) return;

    // Reset betting/action tracking for new street
    this._hadBettingOnCurrentStreet = false;
    this._lastActionOnCurrentStreet = null;

    // Get and clear last action for animation sequence
    const lastAction = getLastStreetAction();
    clearLastStreetAction();

    // Update state immediately (fire-and-forget philosophy)
    setStreet('turn');
    addCommunityCards([card]);
    this.updatePotsFromMessage(message);
    this.emit('streetChanged', { street: 'turn', cards: [card] });

    // Play street-end animation sequence (fire-and-forget)
    streetEndSequencer.playSequence(
      { street: 'turn', cards: [card] },
      lastAction
    );

    // P1.3.5: Emit turn:revealed to GameEventBus for Effects Engine
    gameEventBus.emit('turn:revealed', { card });

    log.game.info`Turn: ${cards[0]}`;
  }

  private handleRiver(message: Record<string, unknown>): void {
    // POKERTALK: {"a":"river","cards":["Tc"],"pots":[...]}
    const cards = message.cards as string[] | undefined;
    if (!cards || cards.length < 1) return;

    const card = this.parseCardCode(cards[0] as CardCode);
    if (!card) return;

    // Reset betting/action tracking for new street
    this._hadBettingOnCurrentStreet = false;
    this._lastActionOnCurrentStreet = null;

    // Get and clear last action for animation sequence
    const lastAction = getLastStreetAction();
    clearLastStreetAction();

    // Update state immediately (fire-and-forget philosophy)
    setStreet('river');
    addCommunityCards([card]);
    this.updatePotsFromMessage(message);
    this.emit('streetChanged', { street: 'river', cards: [card] });

    // Play street-end animation sequence (fire-and-forget)
    streetEndSequencer.playSequence(
      { street: 'river', cards: [card] },
      lastAction
    );

    // P1.3.6: Emit river:revealed to GameEventBus for Effects Engine
    gameEventBus.emit('river:revealed', { card });

    log.game.info`River: ${cards[0]}`;
  }

  /** Parse pots from POKERTALK message and update state */
  private updatePotsFromMessage(message: Record<string, unknown>): void {
    const potsData = message.pots as Array<{
      id: number;
      pot_gross: number;
      participants: string[];
    }> | undefined;

    if (potsData) {
      const pots: PotState[] = potsData.map((p) => ({
        id: p.id,
        amount: p.pot_gross,
        eligiblePlayerIds: p.participants,
      }));
      setPots(pots);
      this.emit('potUpdated', { pots });

      // Emit pot:updated to GameEventBus for Effects Engine
      const totalAmount = pots.reduce((sum, p) => sum + p.amount, 0);
      gameEventBus.emit('pot:updated', { pots, totalAmount });

      log.game.debug`Pot updated: ${pots.map((p) => p.amount).join(', ')}`;
    }
  }

  private handleShow(message: Record<string, unknown>): void {
    // POKERTALK: {"a":"show","player":{"id":"you"},"hole":["Jd","3s"]}
    const player = message.player as { id: string } | undefined;
    const hole = message.hole as string[] | undefined;
    if (!player || !hole || hole.length !== 2) return;

    const card1 = this.parseCardCode(hole[0] as CardCode);
    const card2 = this.parseCardCode(hole[1] as CardCode);
    if (!card1 || !card2) return;

    // Find the seat for this player
    const players = tableState.players.get();
    let seat: number | null = null;
    players.forEach((p, s) => {
      if (p.profile.id === player.id) {
        seat = s;
      }
    });

    // If player id is "you", check hero seat
    if (player.id === 'you') {
      seat = tableState.heroSeat.get();
    }

    if (seat === null) {
      log.game.warning`Show from unknown player: ${player.id}`;
      return;
    }

    const holeCards: [Card, Card] = [card1, card2];
    const playerState = getPlayer(seat);
    if (playerState) {
      updatePlayer(seat, { ...playerState, holeCards });
    }

    // Emit event for UI to reveal cards
    this.emit('cardsRevealed', { seat, cards: holeCards });

    // Emit cards:revealed to GameEventBus for Effects Engine
    gameEventBus.emit('cards:revealed', {
      seat,
      cards: holeCards,
      playerId: player.id,
    });

    log.game.info`${player.id} shows: ${hole[0]}, ${hole[1]}`;
  }

  private handleWin(message: Record<string, unknown>): void {
    const seat = Number(message.seat ?? 0);
    const amount = Number(message.amount ?? 0);
    const handName = message.hand as string | undefined;

    const winnerEvent: { seat: number; amount: number; handName?: string } = { seat, amount };
    if (typeof handName === 'string') {
      winnerEvent.handName = handName;
    }
    this.emit('winner', winnerEvent);
  }

  private handleEnd(message: Record<string, unknown>): void {
    // POKERTALK: End message contains player hole cards and winner info
    // {"a":"end","players":[{"player":{"id":"you"},"hole":["Kc","6d"],...}],"pots":[{"winners":["you"],...}]}

    // Cancel any pending act sound (hand is over)
    this.cancelActSoundTimer();

    log.game.info`handleEnd received: ${JSON.stringify(message)}`;

    // Debug: Log raw made_hand from server
    import('@/utils/DebugOverlay').then(({ debugOverlay }) => {
      const rawPlayers = message.players as Array<{ player: { id: string }; made_hand?: string[] }> | undefined;
      debugOverlay.log(`📥 END: ${rawPlayers?.length ?? 0} players`);
      if (rawPlayers) {
        for (const p of rawPlayers) {
          const madeHand = p.made_hand ?? [];
          debugOverlay.log(`  ${p.player.id}: made_hand=[${madeHand.join(',')}]`);
        }
      }
    });

    // CRITICAL: Capture player bets FIRST, before ANY state updates or events.
    // Events and subscriptions can clear bets synchronously.
    const capturedBets = this.captureCurrentBets();

    const heroSeat = tableState.heroSeat.get();
    const tablePlayers = tableState.players.get();
    const gameNumber = tableState.gameNumber.get();
    const board = tableState.communityCards.get();

    // Parse the end message into GameEndMessage format
    const endMessage = this.parseEndMessage(message, heroSeat, tablePlayers, board);

    // Update player state with hole cards immediately
    for (const playerInfo of endMessage.players) {
      if (playerInfo.holeCards) {
        const playerState = getPlayer(playerInfo.seat);
        if (playerState) {
          updatePlayer(playerInfo.seat, { ...playerState, holeCards: playerInfo.holeCards });
        }
      }
    }

    // Stop the action timer
    this.stopTimer();

    // Emit events for listeners (UI components, etc.)
    this.emitEndEvents(endMessage, gameNumber);

    // Fire-and-forget: Let the sequencer orchestrate all animations
    void gameEndSequencer.playSequence(
      endMessage,
      this._hadBettingOnCurrentStreet,
      this._lastActionOnCurrentStreet,
      capturedBets
    ).then(() => {
      // Update all player stacks from end message balances (winner gets pot winnings).
      // Skip seats that received a br (rebuy/refill) — their stack is already correct.
      for (const playerInfo of endMessage.players) {
        if (isBrUpdated(playerInfo.seat)) continue;
        const playerState = getPlayer(playerInfo.seat);
        if (playerState) {
          updatePlayer(playerInfo.seat, { ...playerState, stack: playerInfo.balance });
        }
      }

      // After animation sequence completes, clear table for next hand
      endHand();
      this.emit('handEnded', undefined);

      // Emit hand:end to GameEventBus for Effects Engine
      gameEventBus.emit('hand:end', { gameNumber });

      // Clear event history for next hand
      gameEventBus.clearHistory();
    });
  }

  /**
   * Parse the raw end message into GameEndMessage format.
   */
  private parseEndMessage(
    message: Record<string, unknown>,
    heroSeat: number | null,
    tablePlayers: Map<number, PlayerState>,
    board: Card[]
  ): GameEndMessage {
    const rawPlayers = message.players as
      | Array<{
          player: { id: string };
          sn: number;
          br?: number;
          pp?: number;
          hole?: string[];
          has_cards?: boolean;
          made_hand?: string[];
          hand_id?: number;
          mr?: number;
        }>
      | undefined;

    const rawPots = message.pots as
      | Array<{
          id: number;
          winners: string[];
          pot_gross: number;
          pot_net: number;
          gain: number;
        }>
      | undefined;

    // Parse players
    const players: EndPlayerInfo[] = [];
    let isShowdown = false;

    if (rawPlayers) {
      for (const p of rawPlayers) {
        const seat = this.findPlayerSeat(p.player.id, heroSeat, tablePlayers);
        if (seat === null) continue;

        let holeCards: [Card, Card] | null = null;
        if (p.hole && p.hole.length === 2) {
          const card1 = this.parseCardCode(p.hole[0] as CardCode);
          const card2 = this.parseCardCode(p.hole[1] as CardCode);
          if (card1 && card2) {
            holeCards = [card1, card2];
            isShowdown = true;
          }
        }

        let coreCards: Card[] | null = null;
        let kickerCards: Card[] | null = null;
        let handName: string | undefined;
        if (p.made_hand && p.made_hand.length > 0) {
          // Server sends 7 cards (hole + board) ordered by strength.
          // Take only the FIRST 5 cards - these form the actual winning hand.
          const first5 = p.made_hand.slice(0, 5);
          const allCards = first5
            .map((code) => this.parseCardCode(code as CardCode))
            .filter((c): c is Card => c !== null);

          // Split into core cards (define the hand) and kicker cards
          const handId = p.hand_id ?? 0;
          const coreCount = getCoreCardCount(handId);
          coreCards = allCards.slice(0, coreCount);
          kickerCards = allCards.slice(coreCount);

          if (typeof p.hand_id === 'number') {
            handName = getHandName(p.hand_id);
          }
          log.game.debug`parseEndMessage: ${p.player.id} parsed ${coreCards.length} core + ${kickerCards.length} kicker cards (hand_id=${handId})`;
        } else {
          log.game.debug`parseEndMessage: ${p.player.id} has no made_hand`;
        }

        const playerInfo: EndPlayerInfo = {
          playerId: p.player.id,
          seat,
          balance: p.br ?? 0,
          potParticipation: p.pp ?? 0,
          moneyReturn: p.mr ?? 0,
          hasCards: p.has_cards ?? false,
          holeCards,
          coreCards,
          kickerCards,
        };
        if (handName !== undefined) {
          playerInfo.handName = handName;
        }
        players.push(playerInfo);
      }
    }

    // Parse pots
    const pots: PotAwardInfo[] = [];
    if (rawPots) {
      for (const pot of rawPots) {
        if (!pot.winners || pot.winners.length === 0) continue;

        const winners: PotAwardInfo['winners'] = [];
        for (const winnerId of pot.winners) {
          const seat = this.findPlayerSeat(winnerId, heroSeat, tablePlayers);
          if (seat !== null) {
            const amount = pot.gain ?? Math.floor(pot.pot_net / pot.winners.length);
            winners.push({ seat, playerId: winnerId, amount });
          }
        }

        if (winners.length > 0) {
          pots.push({
            potId: pot.id,
            winners,
            potAmount: pot.pot_gross,
          });
        }
      }
    }

    return {
      gameNumber: (message.gn as number) ?? tableState.gameNumber.get(),
      board,
      players,
      pots,
      isShowdown,
    };
  }

  /**
   * Emit events for end message (for UI components and effect listeners).
   */
  private emitEndEvents(endMessage: GameEndMessage, _gameNumber: number): void {
    // Emit showdown:start with eligible seats
    const eligibleSeats = endMessage.players
      .filter((p) => p.hasCards || p.holeCards)
      .map((p) => p.seat);
    gameEventBus.emit('showdown:start', { eligibleSeats });

    // NOTE: Card reveal events (showdown:reveal) are NOT emitted here.
    // They are emitted by GameEndSequencer.revealHoleCards() during END-2
    // to ensure proper animation sequencing (END-1 → END-2).

    // Emit money return events (moneyReturn animation is part of END-1)
    for (const player of endMessage.players) {
      if (player.moneyReturn > 0) {
        gameEventBus.emit('player:moneyReturn', {
          seat: player.seat,
          playerId: player.playerId,
          amount: player.moneyReturn,
        });
      }
    }

    // NOTE: Winner/highlight events are NOT emitted here.
    // They are emitted by GameEndSequencer during the animation sequence:
    //   - END-3: showdown:highlight (per pot) - highlights winning cards
    //   - END-4: pot award animations
    // This ensures highlights appear at the correct time in the sequence.
    //
    // Only emit minimal events needed for state tracking (not visual effects):
    for (const pot of endMessage.pots) {
      for (const winner of pot.winners) {
        // Emit winner for balance updates (no visual highlighting)
        this.emit('winner', { seat: winner.seat, amount: winner.amount });
      }
    }
  }

  /**
   * Handle showdown prompt from server.
   * This is sent to losers who still have cards and can choose to show or muck.
   */
  private handleShowdownPrompt(_message: Record<string, unknown>): void {
    log.game.info`handleShowdownPrompt: Hero can show or muck cards`;
    this.emit('showdownPrompt', undefined);
  }

  /**
   * Handle muck from server (a player chose not to show their cards).
   */
  private handleMuck(message: Record<string, unknown>): void {
    const playerId = (message.player as { id: string })?.id;
    if (!playerId) {
      log.game.warning`handleMuck: No player id in message`;
      return;
    }

    const heroSeat = tableState.heroSeat.get();
    const tablePlayers = tableState.players.get();
    const seat = this.findPlayerSeat(playerId, heroSeat, tablePlayers);

    if (seat !== null) {
      log.game.info`handleMuck: Player at seat ${seat} mucked cards`;
      this.emit('cardsMucked', { seat });

      // Emit to GameEventBus for effects
      gameEventBus.emit('showdown:reveal', {
        seat,
        playerId,
        mucked: true,
      });
    } else {
      log.game.warning`handleMuck: Could not find seat for player ${playerId}`;
    }
  }

  /** Helper to find a player's seat by their ID */
  private findPlayerSeat(
    playerId: string,
    heroSeat: number | null,
    tablePlayers: Map<number, PlayerState>
  ): number | null {
    if (playerId === 'you') {
      return heroSeat;
    }
    for (const [seat, player] of tablePlayers) {
      if (player.profile.id === playerId) {
        return seat;
      }
    }
    return null;
  }

  private handlePlayerAction(seat: number, action: ActionType, amount: number): void {
    // Update player state
    const player = getPlayer(seat);
    if (player) {
      const updatedPlayer: PlayerState = {
        ...player,
        lastAction: action,
      };

      if (action === 'fold') {
        // Fold doesn't change bet - just update status
        updatedPlayer.status = 'folded';
      } else if (action === 'check') {
        // Check doesn't change bet - do nothing
      } else if (action === 'allin') {
        updatedPlayer.status = 'allin';
        updatedPlayer.bet += player.stack;
        updatedPlayer.stack = 0;
      } else if (amount > 0) {
        // bet, call, raise actions modify the bet
        const betAmount = Math.min(amount, player.stack);
        updatedPlayer.bet += betAmount;
        updatedPlayer.stack -= betAmount;
      }

      updatePlayer(seat, updatedPlayer);
    }

    // Clear hero actions if hero acted
    const heroSeat = tableState.heroSeat.get();
    if (seat === heroSeat) {
      setHeroActions(null);
    }

    this.stopTimer();
    this.emit('playerActed', { seat, action, amount });
  }

  private sendAction(action: ActionType, amount?: number): void {
    log.game.debug`sendAction(${action}, ${amount}) called`;

    const heroSeat = tableState.heroSeat.get();
    log.game.debug`heroSeat: ${heroSeat}`;
    if (heroSeat === null) {
      log.game.warning`sendAction aborted: heroSeat is null`;
      return;
    }

    // Check it's hero's turn
    const activeSeat = tableState.activeSeat.get();
    log.game.debug`activeSeat: ${activeSeat}`;
    if (activeSeat !== heroSeat) {
      log.game.warning`sendAction aborted: not hero's turn (active: ${activeSeat}, hero: ${heroSeat})`;
      this.emit('error', { message: 'Not your turn' });
      return;
    }

    // Immediately hide action panel and stop timers
    log.game.debug`Hiding action panel`;
    setHeroActions(null);
    this.stopTimer();
    this.cancelActSoundTimer();

    // Send to server if connected (POKERTALK format: # action [amount])
    const isConnected = networkService.isConnected();
    log.game.debug`Network connected: ${isConnected}`;
    if (isConnected) {
      const croupierId = tableState.tableId.get();
      if (!croupierId) {
        log.game.error`Cannot send action - no croupier ID`;
        return;
      }
      const cmd = amount !== undefined ? `# ${action} ${amount}` : `# ${action}`;
      log.game.debug`Sending action to ${croupierId}: ${cmd}`;
      networkService.send(croupierId, cmd);
      // Server will send action confirmation - no local handling needed
      return;
    }

    // For development only (not connected to server): handle locally
    log.game.debug`Handling action locally (dev mode)`;
    this.handlePlayerAction(heroSeat, action, amount ?? 0);

    // Move to next action
    setTimeout(() => {
      const players = tableState.players.get();
      const nextSeat = this.findNextActivePlayer(heroSeat, players);
      if (nextSeat !== null && this.isBettingContinuing(players)) {
        this.startAction(nextSeat, players);
      } else {
        this.advanceStreet();
      }
    }, 500);
  }

  private startAction(seat: number, players: Map<number, PlayerState>): void {
    setActivePlayer(seat);

    const heroSeat = tableState.heroSeat.get();
    if (seat === heroSeat) {
      // Hero's turn
      const stakes = tableState.stakes.get();
      const player = players.get(seat);
      const currentBet = player?.bet ?? 0;
      const maxBet = this.getMaxBet(players);
      const callAmount = maxBet - currentBet;

      const availableActions: AvailableActions = {
        actions: this.getAvailableActions(callAmount, player?.stack ?? 0),
        callAmount,
        minRaise: stakes.bigBlind,
        maxRaise: player?.stack ?? 0,
        timeout: 30,
      };

      setHeroActions(availableActions);
      this.emit('actionRequired', availableActions);
      this.startTimer(seat, 30);
    } else {
      // Opponent's turn - auto-act in development mode
      this.startTimer(seat, 30);
      setTimeout(() => {
        if (tableState.activeSeat.get() === seat) {
          this.mockOpponentAction();
        }
      }, 1500);
    }
  }

  private advanceStreet(): void {
    const currentStreet = tableState.street.get();
    const players = tableState.players.get();

    // Collect bets to pot
    this.collectBetsToPot();

    // Check if hand should end
    const activePlayers = this.countActivePlayers(players);
    if (activePlayers <= 1) {
      this.endHandWithWinner();
      return;
    }

    // Advance to next street
    let newStreet: Street;
    let newCards: Card[] = [];

    switch (currentStreet) {
      case 'preflop':
        newStreet = 'flop';
        newCards = this.generateRandomCards(3);
        setCommunityCards(newCards);
        break;
      case 'flop':
        newStreet = 'turn';
        newCards = this.generateRandomCards(1);
        addCommunityCards(newCards);
        break;
      case 'turn':
        newStreet = 'river';
        newCards = this.generateRandomCards(1);
        addCommunityCards(newCards);
        break;
      case 'river':
        this.goToShowdown();
        return;
      default:
        return;
    }

    setStreet(newStreet);
    this.emit('streetChanged', { street: newStreet, cards: newCards });

    // BLOCKING PATTERN (P0.1.4): Delay before starting next betting round
    // This delay allows community card animations to complete visually.
    // TODO: Replace with Effects Engine orchestration (effects complete → callback → start action)
    setTimeout(() => {
      const dealerSeat = tableState.dealerSeat.get();
      const firstActor = this.findNextActivePlayer(dealerSeat, players);
      if (firstActor !== null) {
        this.startAction(firstActor, players);
      }
    }, GAME_FLOW_TIMING.streetAdvanceDelay);
  }

  private collectBetsToPot(): void {
    const players = tableState.players.get();
    let totalBets = 0;

    players.forEach((player, seat) => {
      totalBets += player.bet;
      updatePlayer(seat, { ...player, bet: 0 });
    });

    const currentPots = tableState.pots.get();
    const mainPot = currentPots[0];
    if (mainPot) {
      setPots([{ ...mainPot, amount: mainPot.amount + totalBets }]);
    } else {
      setPots([{ id: 0, amount: totalBets, eligiblePlayerIds: [] }]);
    }

    this.emit('potUpdated', { pots: tableState.pots.get() });
  }

  private goToShowdown(): void {
    setStreet('showdown');
    this.emit('streetChanged', { street: 'showdown' });

    // In a real game, server would send show messages
    // For development, just end with winner
    setTimeout(() => {
      this.endHandWithWinner();
    }, 1000);
  }

  private endHandWithWinner(): void {
    const players = tableState.players.get();
    const pots = tableState.pots.get();
    const totalPot = pots.reduce((sum, pot) => sum + pot.amount, 0);

    // Find winner (first active player for mock)
    let winnerSeat: number | null = null;
    players.forEach((player, seat) => {
      if (player.status !== 'folded' && winnerSeat === null) {
        winnerSeat = seat;
      }
    });

    if (winnerSeat !== null) {
      const winner = players.get(winnerSeat);
      if (winner) {
        updatePlayer(winnerSeat, { ...winner, stack: winner.stack + totalPot });
        this.emit('winner', { seat: winnerSeat, amount: totalPot });
      }
    }

    // End hand
    setTimeout(() => {
      endHand();
      this.emit('handEnded', undefined);
    }, 2000);
  }

  private startTimer(seat: number, duration: number): void {
    this.stopTimer();
    this.timerRemaining = duration;
    this.timerTotal = duration;

    this.timerInterval = setInterval(() => {
      this.timerRemaining -= 1;
      this.emit('timerTick', {
        seat,
        remaining: this.timerRemaining,
        total: this.timerTotal,
      });

      if (this.timerRemaining <= 0) {
        this.stopTimer();
        // Auto-fold on timeout
        const heroSeat = tableState.heroSeat.get();
        if (seat === heroSeat) {
          this.fold();
        }
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.actionTimer) {
      clearTimeout(this.actionTimer);
      this.actionTimer = null;
    }
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  /**
   * Start delayed "your turn" reminder sound.
   * Sound plays after ACT_SOUND_DELAY if player hasn't acted.
   */
  private startActSoundTimer(): void {
    this.cancelActSoundTimer();
    this.actSoundTimer = setTimeout(() => {
      audioService.playActionBell();
      this.actSoundTimer = null;
    }, ACT_SOUND_DELAY);
  }

  /**
   * Cancel the delayed act sound (player acted before timer fired).
   */
  private cancelActSoundTimer(): void {
    if (this.actSoundTimer) {
      clearTimeout(this.actSoundTimer);
      this.actSoundTimer = null;
    }
  }

  // ===========================================================================
  // PRE-ACTION HELPERS
  // ===========================================================================

  private isPreActionValid(
    preAction: PreActionSelection,
    available: AvailableActions,
  ): boolean {
    switch (preAction.type) {
      case 'fold':
        return available.actions.includes('fold');
      case 'check':
        return available.actions.includes('check');
      case 'checkFold':
        return available.actions.includes('check') || available.actions.includes('fold');
      case 'callAmount':
        return available.actions.includes('call') && available.callAmount === preAction.amount;
      case 'callAny':
        return available.actions.includes('call');
      case 'allin':
        return available.actions.includes('allin');
      default:
        return false;
    }
  }

  private executePreAction(
    preAction: PreActionSelection,
    available: AvailableActions,
  ): void {
    switch (preAction.type) {
      case 'fold':
        this.fold();
        break;
      case 'check':
        this.check();
        break;
      case 'checkFold':
        if (available.actions.includes('check')) {
          this.check();
        } else {
          this.fold();
        }
        break;
      case 'callAmount':
      case 'callAny':
        this.call();
        break;
      case 'allin':
        this.allIn();
        break;
    }
  }

  // ===========================================================================
  // HELPER METHODS
  // ===========================================================================

  private parseCardCode(code: CardCode): Card | null {
    if (!code || code.length !== 2) return null;
    const rank = code[0] as Card['rank'];
    const suit = code[1] as Card['suit'];
    return { rank, suit };
  }

  /**
   * Capture current player bets from tableState for animation.
   * MUST be called at the very START of handleEnd() before any state updates,
   * as state changes and event handlers can clear bets synchronously.
   */
  private captureCurrentBets(): PlayerPotSource[] {
    const playerPots: PlayerPotSource[] = [];
    const players = tableState.players.get();

    players.forEach((player, seat) => {
      if (player.bet > 0) {
        const potPos = targetRegistry.getPlayerPotPosition(seat);
        if (potPos) {
          playerPots.push({
            x: potPos.x,
            y: potPos.y,
            amount: player.bet,
            seatNumber: seat,
          });
        }
      }
    });

    return playerPots;
  }

  private generateRandomCards(count: number): Card[] {
    const ranks: Card['rank'][] = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];
    const suits: Card['suit'][] = ['c', 'd', 'h', 's'];
    const cards: Card[] = [];

    for (let i = 0; i < count; i++) {
      cards.push({
        rank: ranks[Math.floor(Math.random() * ranks.length)] as Card['rank'],
        suit: suits[Math.floor(Math.random() * suits.length)] as Card['suit'],
      });
    }

    return cards;
  }

  private findNextOccupiedSeat(fromSeat: number, players: Map<number, PlayerState>): number {
    const tableSize = tableState.tableSize.get();
    let seat = (fromSeat + 1) % tableSize;
    const startSeat = seat;

    do {
      if (players.has(seat)) {
        return seat;
      }
      seat = (seat + 1) % tableSize;
    } while (seat !== startSeat);

    return fromSeat; // Fallback
  }

  private findNextActivePlayer(fromSeat: number, players: Map<number, PlayerState>): number | null {
    const tableSize = tableState.tableSize.get();
    let seat = (fromSeat + 1) % tableSize;
    const startSeat = seat;

    do {
      const player = players.get(seat);
      if (player && player.status !== 'folded' && player.status !== 'allin') {
        return seat;
      }
      seat = (seat + 1) % tableSize;
    } while (seat !== startSeat);

    return null;
  }

  private countActivePlayers(players: Map<number, PlayerState>): number {
    let count = 0;
    players.forEach((player) => {
      if (player.status !== 'folded') {
        count++;
      }
    });
    return count;
  }

  private getMaxBet(players: Map<number, PlayerState>): number {
    let maxBet = 0;
    players.forEach((player) => {
      if (player.bet > maxBet) {
        maxBet = player.bet;
      }
    });
    return maxBet;
  }

  private getAvailableActions(callAmount: number, stack: number): ActionType[] {
    const actions: ActionType[] = ['fold'];

    if (callAmount === 0) {
      actions.push('check');
    } else {
      actions.push('call');
    }

    if (stack > callAmount) {
      actions.push('raise');
    }

    actions.push('allin');

    return actions;
  }

  private isBettingContinuing(players: Map<number, PlayerState>): boolean {
    // Check if all active players have equal bets
    let maxBet = 0;
    let allEqual = true;
    let activeCount = 0;

    players.forEach((player) => {
      if (player.status !== 'folded') {
        if (activeCount === 0) {
          maxBet = player.bet;
        } else if (player.bet !== maxBet && player.status !== 'allin') {
          allEqual = false;
        }
        maxBet = Math.max(maxBet, player.bet);
        activeCount++;
      }
    });

    return !allEqual && activeCount > 1;
  }

  private postBlind(seat: number, amount: number, type: 'sb' | 'bb'): void {
    const player = getPlayer(seat);
    if (!player) return;

    const blindAmount = Math.min(amount, player.stack);
    updatePlayer(seat, {
      ...player,
      bet: blindAmount,
      stack: player.stack - blindAmount,
      position: type,
    });
  }

  private weightedRandom<T>(items: T[], weights: number[]): T {
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    let random = Math.random() * totalWeight;

    for (let i = 0; i < items.length; i++) {
      random -= weights[i] ?? 0;
      if (random <= 0) {
        return items[i] as T;
      }
    }

    return items[0] as T;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // NOTE: Chip animation for betting actions moved to TableScreen
  // @see tasks/active/T22_BET_CHIP_ANIMATION_PLAN.md
}

/** Singleton game controller instance */
export const gameController = GameController.getInstance();
